import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { 
    Box, 
    Button, 
    Card, 
    TextField, 
    Typography, 
    Divider,
    Paper,
    Stack,
    IconButton,
    Checkbox,
    FormControlLabel,
    Chip,
    Alert,
    Fade,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Switch,
    Snackbar
} from '@mui/material';
import DefaultTable from '../../../component/table/DefaultTable';
import SelectAutocomplete from '../../../component/autocomplete/SelectAutocomplete';
import { useTheme } from '@mui/material/styles';
import { useFormik } from 'formik';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import { httpClient } from '../../../common/httpclient';
import PageScaffold from '../../../component/pageScaffold/PageScaffold';
import ChecklistIcon from '@mui/icons-material/Checklist';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import GroupIcon from '@mui/icons-material/Group';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import InventoryIcon from '@mui/icons-material/Inventory';
import Tooltip from '@mui/material/Tooltip';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useRecoilValue } from 'recoil';
import {
    ArrowLeft,
    GripVertical,
    Info,
    Pencil,
    Plus,
    Star,
    Trash2,
} from 'lucide-react';
import { sidebarExpandedSelector } from '../../../component/layout/sidebarState';
import { useAppBarTitle } from '../../../component/layout/AppBarActions';
import { MobileChecklistForm } from '../../../component/checklist/MobileChecklist';
import useIsMobileShell from '../../../hooks/useIsMobileShell';

// Componente Skeleton para o formulário
const ChecklistFormSkeleton = () => {
    const theme = useTheme();
    
    return (
        <Box sx={{ width: '100%', p: 2 }}>
            {/* Header Skeleton */}
            <Paper 
                elevation={0}
                sx={{ 
                    mb: 3, 
                    borderRadius: 3,
                    background: theme.palette.mode === 'dark' 
                        ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: theme.palette.mode === 'dark' ? 'text.primary' : 'white',
                    border: theme.palette.mode === 'dark' 
                        ? '1px solid rgba(255,255,255,0.1)'
                        : 'none',
                    backdropFilter: theme.palette.mode === 'dark' ? 'blur(10px)' : 'none'
                }}
            >
                <Box sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Skeleton variant="circular" width={40} height={40} />
                        <Box sx={{ flex: 1 }}>
                            <Skeleton variant="text" width="60%" height={40} />
                            <Skeleton variant="text" width="40%" height={24} />
                        </Box>
                    </Stack>
                </Box>
            </Paper>

            {/* Form Skeleton */}
            <Paper 
                elevation={2}
                sx={{ 
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: theme.palette.mode === 'dark' 
                        ? '0 4px 20px rgba(0,0,0,0.3)' 
                        : '0 4px 20px rgba(0,0,0,0.08)'
                }}
            >
                {/* Checklist Information Section Skeleton */}
                <Box sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                        <Skeleton variant="circular" width={24} height={24} />
                        <Skeleton variant="text" width={200} height={32} />
                    </Stack>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                                <Skeleton variant="text" width="100%" height={56} />
                            </Box>
                            <Box sx={{ flex: '2 1 400px', minWidth: 0 }}>
                                <Skeleton variant="text" width="100%" height={56} />
                            </Box>
                            <Box sx={{ flex: '0 0 150px', minWidth: 0 }}>
                                <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Divider />

                {/* Inspection Groups Section Skeleton */}
                <Box sx={{ 
                    p: 3, 
                    backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.02)' 
                        : 'grey.50' 
                }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                        <Skeleton variant="circular" width={24} height={24} />
                        <Skeleton variant="text" width={180} height={32} />
                    </Stack>

                    {/* Add Group Form Skeleton */}
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
                        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                            <Skeleton variant="text" width="100%" height={56} />
                        </Box>
                        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                            <Skeleton variant="text" width="100%" height={56} />
                        </Box>
                        <Box sx={{ flex: '0 0 auto', minWidth: 150 }}>
                            <Skeleton variant="rectangular" width={150} height={56} sx={{ borderRadius: 1 }} />
                        </Box>
                    </Box>

                    {/* Groups List Skeleton */}
                    <Box sx={{ mt: 3 }}>
                        <Skeleton variant="text" width={150} height={24} sx={{ mb: 2 }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {[1, 2, 3].map((index) => (
                                <Paper
                                    key={index}
                                    elevation={1}
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: theme.palette.divider,
                                        backgroundColor: theme.palette.background.paper
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
                                        <Skeleton variant="text" width="60%" height={24} />
                                        <Skeleton variant="circular" width={32} height={32} />
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    </Box>
                </Box>

                {/* Action Buttons Skeleton */}
                <Box sx={{ 
                    p: 3, 
                    backgroundColor: theme.palette.background.paper,
                    borderTop: '1px solid',
                    borderColor: theme.palette.divider
                }}>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Skeleton variant="rectangular" width={100} height={48} sx={{ borderRadius: 2 }} />
                        <Skeleton variant="rectangular" width={120} height={48} sx={{ borderRadius: 2 }} />
                    </Stack>
                </Box>
            </Paper>
        </Box>
    );
};

// Componente de linha arrastável para a tabela de grupos
function SortableRow({ group, index, onDelete }) {
    const theme = useTheme();
    // Usar um ID único baseado no índice para garantir que cada item tenha um ID único
    const uniqueId = `group-${index}-${group.id || index}`;
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: uniqueId });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <TableRow
            ref={setNodeRef}
            style={style}
            sx={{
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.05)' 
                        : 'rgba(25,118,210,0.04)',
                },
                '&:nth-of-type(even)': {
                    backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.02)' 
                        : 'rgba(0,0,0,0.02)'
                },
                cursor: isDragging ? 'grabbing' : 'grab',
            }}
        >
            <TableCell
                sx={{
                    py: 1.5,
                    px: 2,
                    width: 50,
                    cursor: 'grab',
                    '&:active': {
                        cursor: 'grabbing',
                    },
                }}
                {...attributes}
                {...listeners}
            >
                <DragIndicatorIcon 
                    sx={{ 
                        color: 'text.secondary',
                        '&:hover': {
                            color: 'primary.main',
                        },
                    }} 
                />
            </TableCell>
            <TableCell 
                sx={{
                    py: 1.5,
                    px: 2,
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    fontWeight: 400
                }}
            >
                {group.name}
            </TableCell>
            <TableCell 
                align="center"
                sx={{
                    py: 1.5,
                    px: 2,
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    fontWeight: 400
                }}
            >
                <Chip
                    label={group.order}
                    color="primary"
                    size="small"
                    variant="outlined"
                />
            </TableCell>
            <TableCell 
                align="center"
                sx={{
                    py: 1.5,
                    px: 2,
                }}
            >
                <IconButton
                    onClick={() => onDelete(index)}
                    size="small"
                    sx={{
                        color: 'error.main',
                        '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' 
                                ? 'rgba(211, 47, 47, 0.1)' 
                                : 'rgba(211, 47, 47, 0.08)',
                        },
                    }}
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </TableCell>
        </TableRow>
    );
}

// ============================================================================
// LEGACY EMBEDDED MODE — mantido 1:1 (nenhuma linha de lógica/JSX alterada
// além do rename da função e da assinatura de props) para não regredir o
// único consumidor restante: `pages/vehicles/checklist/View.js` (item 132,
// "Visualização de template", ainda não redesenhado) embute este formulário
// dentro de um `Drawer` (`<ChecklistForm embedded checklistId={id}
// onClose={...} onSuccess={...} />`). O redesign do item 129
// (FRONTEND-TASKS.md "Templates de checklist — editor") descrito abaixo
// (`StandaloneChecklistForm`) cobre apenas as rotas de página cheia
// `/vehicle_checklist/new` e `/checklist/:id/edt` — a própria tarefa já
// antecipa que quando o item 132 for prototipado, `View.js` deve passar a
// navegar para o editor de página cheia em vez de embutir este drawer, mas
// isso é decisão daquele item, não deste.
// ============================================================================
function LegacyEmbeddedChecklistForm({ checklistId, onClose, onSuccess }) {
    const embedded = true;
    const theme = useTheme();
    const navigate = useNavigate();
    const id = checklistId;
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('');
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [inspectionGroups, setInspectionGroups] = useState([]);
    const [availableGroups, setAvailableGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [groupOrder, setGroupOrder] = useState('');

    // Configuração dos sensores para drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const validationSchema = Yup.object({
        name: Yup.string().required('Nome do checklist é obrigatório'),
        description: Yup.string().required('Descrição é obrigatória'),
        is_default: Yup.boolean()
    });

    const handleShowSuccessSnackbar = () => {
        if (embedded) {
            return;
        }
        setOpenSnackbar(true);
        if (id) {
            setMessage('Checklist atualizado com sucesso!');
        } else {
            setMessage('Checklist cadastrado com sucesso!');
        }
        setSeverity('success');
    };

    const handleShowErrorSnackbar = (errorMessage) => {
        setOpenSnackbar(true);
        const text =
            typeof errorMessage === 'string'
                ? errorMessage
                : errorMessage != null
                    ? JSON.stringify(errorMessage)
                    : 'Erro ao processar a solicitação';
        setMessage(text);
        setSeverity('error');
    };

    const fetchChecklist = async () => {
        try {
            setLoading(true);
            const response = await httpClient.get(`/checklist/${id}`);
            const checklistData = response.data;
            
            formik.setValues({
                name: checklistData.name || '',
                description: checklistData.description || '',
                is_default: checklistData.is_default || false
            });
            
            if (checklistData.groups) {
                setInspectionGroups(checklistData.groups);
            }
        } catch (error) {
            console.error('Error fetching checklist:', error);
            handleShowErrorSnackbar('Erro ao carregar dados do checklist');
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    };

    const fetchAvailableGroups = async () => {
        try {
            const response = await httpClient.get('/checklist/groups');
            setAvailableGroups(response.data);
        } catch (error) {
            console.error('Error fetching available groups:', error);
        } finally {
            if (!id) {
                setInitialLoading(false);
            }
        }
    };

    const formik = useFormik({
        initialValues: {
            name: '',
            description: '',
            is_default: false
        },
        validationSchema: validationSchema,
        onSubmit: (values) => {
            if (id) {
                handleUpdateChecklist(values);
            } else {
                handleCreateChecklist(values);
            }
        },
    });

    const handleCreateChecklist = async (values) => {
        try {
            setLoading(true);
            const data = {
                name: values.name,
                description: values.description,
                is_default: values.is_default,
                groups: inspectionGroups
            };

            const response = await httpClient.post('/checklist', data);
            if (response.status === 201 || response.status === 200) {
                if (embedded && onSuccess) {
                    onSuccess(response.data);
                } else {
                    handleShowSuccessSnackbar();
                    setTimeout(() => {
                        navigate(`/checklist/${response.data.id}/edt?success=true`);
                    }, 1500);
                }
            } else {
                handleShowErrorSnackbar(response.data || 'Erro ao criar checklist');
            }
        } catch (error) {
            console.error('Error creating checklist:', error);
            const errorMessage = error.response?.data || error.message || 'Erro ao criar checklist';
            handleShowErrorSnackbar(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateChecklist = async (values) => {
        try {
            setLoading(true);
            const data = {
                name: values.name,
                description: values.description,
                is_default: values.is_default,
                groups: inspectionGroups
            };
            const response = await httpClient.put(`/checklist/${id}`, data);
            if (response.status === 204 || response.status === 200) {
                if (embedded && onSuccess) {
                    try {
                        const res = await httpClient.get(`/checklist/${id}`);
                        onSuccess(res.data);
                    } catch (e) {
                        onSuccess({
                            id,
                            name: values.name,
                            description: values.description,
                            is_default: values.is_default,
                            groups: inspectionGroups
                        });
                    }
                } else {
                    handleShowSuccessSnackbar();
                    setTimeout(() => {
                        navigate('/vehicle_checklist');
                    }, 1500);
                }
            } else {
                handleShowErrorSnackbar(response.data || 'Erro ao atualizar checklist');
            }
        } catch (error) {
            console.error('Error updating checklist:', error);
            const errorMessage = error.response?.data || error.message || 'Erro ao atualizar checklist';
            handleShowErrorSnackbar(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const calculateNextOrder = () => {
        if (inspectionGroups.length === 0) {
            return 1;
        }
        // Encontrar a maior ordem existente e incrementar
        const maxOrder = Math.max(...inspectionGroups.map(group => group.order || 0));
        return maxOrder + 1;
    };

    const addInspectionGroup = () => {
        if (selectedGroupId) {
            
            const selectedGroup = availableGroups.find(group => group.id === selectedGroupId);
            if (selectedGroup) {
                // Verificar se o grupo já foi adicionado
                const groupAlreadyExists = inspectionGroups.some(group => group.id === selectedGroupId);
                if (groupAlreadyExists) {
                    handleShowErrorSnackbar('Este grupo já foi adicionado ao checklist');
                    return;
                }

                // Usar a ordem informada pelo usuário ou calcular automaticamente
                const order = groupOrder ? parseInt(groupOrder, 10) : calculateNextOrder();

                const newGroup = {
                    id: selectedGroup.id,
                    name: selectedGroup.name,
                    order: order
                };
                ;
                
                const updatedGroups = [...inspectionGroups, newGroup];
                // Ordenar grupos por ordem
                updatedGroups.sort((a, b) => a.order - b.order);
                setInspectionGroups(updatedGroups);
                
                setSelectedGroupId('');
                setGroupOrder('');
            }
        }
    };

    const removeInspectionGroup = (index) => {
        const updatedGroups = inspectionGroups.filter((_, i) => i !== index);
        // Reordenar os grupos após remoção
        const reorderedGroups = updatedGroups.map((group, idx) => ({
            ...group,
            order: idx + 1
        }));
        setInspectionGroups(reorderedGroups);
    };

    // Handler para quando um grupo é arrastado
    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setInspectionGroups((items) => {
                // Extrair o índice do ID único (formato: "group-{index}-{id}")
                const getIndexFromId = (id) => {
                    const match = id.toString().match(/group-(\d+)-/);
                    return match ? parseInt(match[1], 10) : -1;
                };
                
                const oldIndex = getIndexFromId(active.id);
                const newIndex = getIndexFromId(over.id);
                
                if (oldIndex === -1 || newIndex === -1) {
                    return items;
                }
                
                const newItems = arrayMove(items, oldIndex, newIndex);
                
                // Atualizar a ordem dos grupos
                return newItems.map((group, index) => ({
                    ...group,
                    order: index + 1
                }));
            });
        }
    };

    const cleanFields = () => {
        formik.resetForm();
        setInspectionGroups([]);
        setSelectedGroupId('');
        setGroupOrder('');
    };

    const breadcrumbs = [
        { title: 'Veículos', link: '/vehicles' },
        { title: 'Checklists', link: '/vehicle_checklist' },
    ];

    useEffect(() => {
        const initializeData = async () => {
            try {
                await fetchAvailableGroups();
                if (id) {
                    await fetchChecklist();
                } else {
                    setInitialLoading(false);
                }
            } catch (error) {
                console.error('Error initializing data:', error);
                setInitialLoading(false);
            }
        };

        initializeData();
    }, [id]);

    if (initialLoading) {
        if (embedded) {
            return (
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        backgroundColor: theme.palette.background.default
                    }}
                >
                    <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 2 }}>
                        <ChecklistFormSkeleton />
                    </Box>
                </Box>
            );
        }
        return (
            <PageScaffold 
                title={id ? 'Editar Checklist de Inspeção' : 'Novo Checklist de Inspeção'} 
                titlePageTarget={id ? 'Editar' : 'Novo'} 
                links={breadcrumbs} 
                message={message} 
                severity={severity} 
                openSnackbar={openSnackbar} 
                setOpenSnackbar={setOpenSnackbar}
            >
                <ChecklistFormSkeleton />
            </PageScaffold>
        );
    }

    const formContent = (
        <Box sx={{ width: '100%' }}>
            <Box
                sx={{
                    width: '100%',
                    ...(embedded ? { px: 2, pb: 2 } : { p: 2 })
                }}
            >
                {embedded ? (
                    <Box
                        sx={{
                            flexShrink: 0,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: theme.palette.background.paper,
                            pt: 3
                        }}
                    >
                        <Box sx={{ pt: 1.5, pb: 0.5, display: 'flex', justifyContent: 'center' }}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 4,
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.2)'
                                        : 'rgba(0,0,0,0.12)'
                                }}
                            />
                        </Box>
                        <Box
                            sx={{
                                px: 2,
                                pt: 5,
                                pb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
                                    {id ? 'Editar Checklist' : 'Novo Checklist'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {id
                                        ? 'Atualize as informações do checklist'
                                        : 'Crie um novo checklist de inspeção para veículos'}
                                </Typography>
                            </Box>
                            <IconButton
                                onClick={() => onClose && onClose()}
                                size="medium"
                                sx={{
                                    color: 'text.secondary',
                                    '&:hover': { backgroundColor: theme.palette.action.hover }
                                }}
                                aria-label="Fechar"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>
                ) : (
                    <Paper 
                        elevation={0}
                        sx={{ 
                            mb: 3, 
                            borderRadius: 3,
                            background: theme.palette.mode === 'dark' 
                                ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: theme.palette.mode === 'dark' ? 'text.primary' : 'white',
                            border: theme.palette.mode === 'dark' 
                                ? '1px solid rgba(255,255,255,0.1)'
                                : 'none',
                            backdropFilter: theme.palette.mode === 'dark' ? 'blur(10px)' : 'none'
                        }}
                    >
                        <Box sx={{ p: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <IconButton
                                    onClick={() => navigate('/vehicle_checklist')}
                                    sx={{ 
                                        color: theme.palette.mode === 'dark' ? 'text.primary' : 'white',
                                        backgroundColor: theme.palette.mode === 'dark' 
                                            ? 'rgba(255,255,255,0.05)' 
                                            : 'rgba(255,255,255,0.1)',
                                        '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? 'rgba(255,255,255,0.1)'
                                                : 'rgba(255,255,255,0.2)'
                                        }
                                    }}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                                        {id ? 'Editar Checklist' : 'Dados do Checklist'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        {id ? 'Atualize as informações do checklist' : 'Crie um novo checklist de inspeção para veículos'}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    </Paper>
                )}

                <Paper 
                    elevation={embedded ? 0 : 2}
                    sx={{ 
                        borderRadius: embedded ? 2 : 3,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: theme.palette.divider,
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: embedded
                            ? 'none'
                            : (theme.palette.mode === 'dark' 
                                ? '0 4px 20px rgba(0,0,0,0.3)' 
                                : '0 4px 20px rgba(0,0,0,0.08)'),
                        ...(embedded && { mb: 0 })
                    }}
                >
                    <form onSubmit={formik.handleSubmit}>
                        {/* Checklist Information Section */}
                        <Box sx={{ p: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                                <ChecklistIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Informações do Checklist
                                </Typography>
                            </Stack>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {/* First Row */}
                                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                    {/* Checklist Name */}
                                    <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                                        <TextField
                                            fullWidth
                                            label="Nome do Checklist"
                                            value={formik.values.name}
                                            onChange={formik.handleChange}
                                            name="name"
                                            error={formik.touched.name && Boolean(formik.errors.name)}
                                            helperText={formik.touched.name && formik.errors.name}
                                            variant="standard"
                                            size="medium"
                                            required
                                        />
                                    </Box>

                                    {/* Description */}
                                    <Box sx={{ flex: '2 1 400px', minWidth: 0 }}>
                                        <TextField
                                            fullWidth
                                            label="Descrição"
                                            value={formik.values.description}
                                            onChange={formik.handleChange}
                                            name="description"
                                            error={formik.touched.description && Boolean(formik.errors.description)}
                                            helperText={formik.touched.description && formik.errors.description}
                                            variant="standard"
                                            size="medium"
                                            required
                                        />
                                    </Box>

                                    {/* Default Switch */}
                                    <Box sx={{ flex: '0 0 150px', minWidth: 0 }}>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            height: '100%',
                                            gap: 2,
                                            mt: 2
                                        }}>
                                           
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={formik.values.is_default}
                                                        onChange={formik.handleChange}
                                                        name="is_default"
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                                color: 'primary.main',
                                                            },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                backgroundColor: 'primary.main',
                                                            },
                                                        }}
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2" sx={{ 
                                                        fontWeight: 500,
                                                        color: theme.palette.text.primary
                                                    }}>
                                                        Padrão
                                                    </Typography>
                                                }
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        <Divider />

                        {/* Inspection Groups Section */}
                        <Box sx={{ 
                            p: 3, 
                            backgroundColor: theme.palette.mode === 'dark' 
                                ? 'rgba(255,255,255,0.02)' 
                                : 'grey.50' 
                        }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                                <GroupIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Grupos de Inspeção
                                </Typography>
                            </Stack>

                            {/* Add Group Form */}
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
                                <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                                    {availableGroups.length === 0 ? (
                                        <Skeleton variant="text" width="100%" height={56} />
                                    ) : (
                                        <SelectAutocomplete
                                            options={availableGroups.filter(group => 
                                                !inspectionGroups.some(addedGroup => addedGroup.id === group.id)
                                            )}
                                            value={selectedGroupId}
                                            onChange={(value) => {
                                                setSelectedGroupId(value);
                                                // Preencher automaticamente o campo ordem quando um grupo é selecionado
                                                if (value) {
                                                    setGroupOrder(calculateNextOrder().toString());
                                                } else {
                                                    setGroupOrder('');
                                                }
                                            }}
                                            label="Nome do Grupo"
                                            variant="standard"
                                            size="medium"
                                            getOptionLabel={(option) => option.name || ''}
                                            getOptionValue={(option) => option.id || ''}
                                            noOptionsText={
                                                availableGroups.filter(group => 
                                                    !inspectionGroups.some(addedGroup => addedGroup.id === group.id)
                                                ).length === 0 
                                                    ? 'Todos os grupos já foram adicionados' 
                                                    : 'Nenhum grupo encontrado'
                                            }
                                            placeholder="Selecione ou digite para buscar um grupo"
                                            disabled={availableGroups.filter(group => 
                                                !inspectionGroups.some(addedGroup => addedGroup.id === group.id)
                                            ).length === 0}
                                        />
                                    )}
                                </Box>
                                <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                                    {availableGroups.length === 0 ? (
                                        <Skeleton variant="text" width="100%" height={56} />
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Ordem"
                                            type="number"
                                            value={groupOrder}
                                            onChange={(e) => setGroupOrder(e.target.value)}
                                            variant="standard"
                                            size="medium"
                                            placeholder="1, 2, 3..."
                                            inputProps={{ min: 1 }}
                                        />
                                    )}
                                </Box>
                                <Box sx={{ flex: '0 0 auto', minWidth: 150 }}>
                                    {availableGroups.length === 0 ? (
                                        <Skeleton variant="rectangular" width={150} height={56} sx={{ borderRadius: 1 }} />
                                    ) : (
                                        <Tooltip title="Adicionar Grupo">   
                                        <IconButton
                                            onClick={addInspectionGroup}
                                            disabled={!selectedGroupId || availableGroups.filter(group => !inspectionGroups.some(addedGroup => addedGroup.id === group.id)).length === 0}
                                            sx={{ 
                                                width: 36,
                                                height: 36,
                                                borderRadius: 2,
                                                border: 1,
                                                borderColor: 'primary.main',
                                                color: 'primary.main',
                                                '&:hover': {
                                                    borderColor: 'primary.dark',
                                                    backgroundColor: theme.palette.mode === 'dark' 
                                                        ? 'rgba(25, 118, 210, 0.08)' 
                                                        : 'primary.50'
                                                }
                                            }}
                                        >
                                            <AddIcon />
                                        </IconButton>
                                        </Tooltip>
                                        
                                    )}
                                </Box>
                            </Box>

                            {/* Groups List */}
                            {inspectionGroups.length > 0 && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                        Grupos Adicionados ({inspectionGroups.length})
                                    </Typography>
                                    <Paper
                                        elevation={1}
                                        sx={{
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            border: '1px solid',
                                            borderColor: theme.palette.divider,
                                        }}
                                    >
                                        <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <TableContainer>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell
                                                                sx={{
                                                                    backgroundColor: theme.palette.mode === 'dark' 
                                                                        ? 'rgba(255,255,255,0.05)' 
                                                                        : 'rgba(25,118,210,0.08)',
                                                                    color: theme.palette.mode === 'dark' ? 'text.primary' : 'primary.main',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.875rem',
                                                                    letterSpacing: '0.5px',
                                                                    textTransform: 'uppercase',
                                                                    borderBottom: '2px solid',
                                                                    borderColor: 'primary.main',
                                                                    py: 2,
                                                                    px: 2,
                                                                    width: 50,
                                                                }}
                                                            >
                                                                <DragIndicatorIcon sx={{ fontSize: 20 }} />
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    backgroundColor: theme.palette.mode === 'dark' 
                                                                        ? 'rgba(255,255,255,0.05)' 
                                                                        : 'rgba(25,118,210,0.08)',
                                                                    color: theme.palette.mode === 'dark' ? 'text.primary' : 'primary.main',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.875rem',
                                                                    letterSpacing: '0.5px',
                                                                    textTransform: 'uppercase',
                                                                    borderBottom: '2px solid',
                                                                    borderColor: 'primary.main',
                                                                    py: 2,
                                                                    px: 2,
                                                                }}
                                                            >
                                                                Nome do Grupo
                                                            </TableCell>
                                                            <TableCell
                                                                align="center"
                                                                sx={{
                                                                    backgroundColor: theme.palette.mode === 'dark' 
                                                                        ? 'rgba(255,255,255,0.05)' 
                                                                        : 'rgba(25,118,210,0.08)',
                                                                    color: theme.palette.mode === 'dark' ? 'text.primary' : 'primary.main',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.875rem',
                                                                    letterSpacing: '0.5px',
                                                                    textTransform: 'uppercase',
                                                                    borderBottom: '2px solid',
                                                                    borderColor: 'primary.main',
                                                                    py: 2,
                                                                    px: 2,
                                                                }}
                                                            >
                                                                Ordem
                                                            </TableCell>
                                                            <TableCell
                                                                align="center"
                                                                sx={{
                                                                    backgroundColor: theme.palette.mode === 'dark' 
                                                                        ? 'rgba(255,255,255,0.05)' 
                                                                        : 'rgba(25,118,210,0.08)',
                                                                    color: theme.palette.mode === 'dark' ? 'text.primary' : 'primary.main',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.875rem',
                                                                    letterSpacing: '0.5px',
                                                                    textTransform: 'uppercase',
                                                                    borderBottom: '2px solid',
                                                                    borderColor: 'primary.main',
                                                                    py: 2,
                                                                    px: 2,
                                                                }}
                                                            >
                                                                Ações
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        <SortableContext
                                                            items={inspectionGroups.map((group, index) => `group-${index}-${group.id || index}`)}
                                                            strategy={verticalListSortingStrategy}
                                                        >
                                                            {inspectionGroups.map((group, index) => (
                                                                <SortableRow
                                                                    key={`group-${index}-${group.id || index}`}
                                                                    group={group}
                                                                    index={index}
                                                                    onDelete={removeInspectionGroup}
                                                                />
                                                            ))}
                                                        </SortableContext>
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </DndContext>
                                    </Paper>
                                </Box>
                            )}

                            {inspectionGroups.length === 0 && availableGroups.length === 0 && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    Não há grupos de inspeção cadastrados. Cadastre grupos primeiro para poder adicioná-los ao checklist.
                                </Alert>
                            )}
                            {inspectionGroups.length === 0 && availableGroups.length > 0 && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    Adicione grupos de inspeção para organizar os itens do checklist.
                                </Alert>
                            )}
                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{ 
                            p: embedded ? 2 : 3, 
                            backgroundColor: theme.palette.background.paper,
                            borderTop: '1px solid',
                            borderColor: theme.palette.divider
                        }}>
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    startIcon={embedded ? <CloseIcon /> : <ArrowBackIcon />}
                                    onClick={() => (embedded && onClose ? onClose() : navigate('/vehicle_checklist'))}
                                    sx={{ 
                                        px: embedded ? 2.5 : 2,
                                        py: embedded ? 1.25 : 1.2,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderColor: theme.palette.mode === 'dark' 
                                            ? 'rgba(255,255,255,0.2)' 
                                            : 'rgba(0,0,0,0.1)',
                                        color: theme.palette.text.primary,
                                        '&:hover': {
                                            borderColor: theme.palette.primary.main,
                                            backgroundColor: theme.palette.mode === 'dark' 
                                                ? 'rgba(25, 118, 210, 0.08)' 
                                                : 'rgba(25, 118, 210, 0.04)',
                                        }
                                    }}
                                >
                                    {embedded ? 'Fechar' : 'Voltar'}
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={loading ? <Skeleton variant="circular" width={20} height={20} /> : <SaveIcon />}
                                    onClick={formik.handleSubmit}
                                    disabled={!formik.isValid || !formik.dirty || loading}
                                    sx={{ 
                                        px: embedded ? 2.5 : 3,
                                        py: embedded ? 1.25 : 1.5,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        backgroundColor: theme.palette.primary.main,
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.dark
                                        }
                                    }}
                                >
                                    {loading ? 'Salvando...' : (id ? 'Atualizar' : 'Salvar')}
                                </Button>
                            </Stack>
                        </Box>
                    </form>
                </Paper>
            </Box>
        </Box>
    );

    if (embedded) {
        return (
            <>
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        backgroundColor: theme.palette.background.default
                    }}
                >
                    <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        {formContent}
                    </Box>
                </Box>
                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={6000}
                    onClose={() => setOpenSnackbar(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert
                        onClose={() => setOpenSnackbar(false)}
                        severity={severity || 'error'}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {typeof message === 'string' ? message : JSON.stringify(message)}
                    </Alert>
                </Snackbar>
            </>
        );
    }

    return (
        <PageScaffold 
            title={id ? 'Editar Checklist de Inspeção' : 'Novo Checklist de Inspeção'} 
            titlePageTarget={id ? 'Editar' : 'Novo'} 
            links={breadcrumbs} 
            message={message} 
            severity={severity} 
            openSnackbar={openSnackbar} 
            setOpenSnackbar={setOpenSnackbar}
        >
            <Fade in={!initialLoading} timeout={300}>
                {formContent}
            </Fade>
        </PageScaffold>
    );
}

// ============================================================================
// STANDALONE EDITOR (item 129, "Templates de checklist — editor") — página
// cheia usada pelas rotas `/vehicle_checklist/new` (sem id) e
// `/checklist/:id/edt` (com id). DESIGN-SYSTEM.md §5.5/§6.2 +
// `prototipo-checklist-editor.html`: `form-hero` (voltar + título + anel de
// progresso por 2 critérios) → 2 cards simples (SEM acordeão — só 2 seções
// com poucos campos, deliberadamente mais enxuto que Cliente/Produto/
// Fornecedor) → tabela de grupos reordenável via drag-and-drop HTML5 nativo
// (sem biblioteca, ao contrário do `LegacyEmbeddedChecklistForm` acima que
// usa `@dnd-kit` — ver nota de decisão no relatório da tarefa) → preview
// sticky reaproveitando o padrão `check-group`/`check-group-head` (mesmo
// componente visual da aba Checklist de `Operator.js`) em modo
// somente-leitura, mostrando só nome+contagem de cada grupo.
// ============================================================================

// Casca visual "ff-input"/"ff-textarea" — cópia 1:1 de `standaloneFieldSx` já
// usada em `categories/Form.js`/`suppliers/Form.js`/`services/Form.js`/
// `card_machines/Form.js` (e do extinto `SimpleForm.js` drawer, removido
// junto desta tarefa — ver relatório).
function useStandaloneFieldSx() {
    const standaloneInputBoxSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: 'background.subtle',
            height: '52px',
            transition: 'background-color .15s ease, box-shadow .15s ease',
            '&.MuiInputBase-multiline': {
                height: 'auto',
                minHeight: '96px',
                alignItems: 'flex-start',
                padding: '24px 14px 7px 14px',
            },
        },
        '& .MuiOutlinedInput-input:not(textarea):not(.MuiSelect-select)': {
            padding: '21px 14px 7px 14px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1.5px',
            borderColor: 'divider',
        },
        '& .MuiOutlinedInput-notchedOutline legend': {
            display: 'none',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
            borderWidth: '1.5px',
        },
        '& .MuiInputBase-multiline textarea': {
            resize: 'vertical',
        },
    };

    return {
        '& .MuiInputBase-input': { fontSize: '13.5px' },
        '& .MuiInputLabel-root': {
            fontSize: '13.5px',
            color: 'text.disabled',
            transform: 'translate(14px, 17px) scale(1)',
        },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            fontSize: '10px',
            fontWeight: 800,
            color: 'primary.main',
            letterSpacing: '.04em',
            textTransform: 'uppercase',
            transform: 'translate(14px, 8px) scale(1)',
        },
        ...standaloneInputBoxSx,
    };
}

function parseApiErrorPayload(data) {
    if (data == null) return 'Erro desconhecido';
    if (typeof data === 'string') return data;
    if (typeof data === 'object') {
        return data.message || data.error || data.id || 'Erro ao processar a solicitação';
    }
    return String(data);
}

// Pill "Padrão" compacta (verde + estrela) — mesma convenção de `DefaultPill`
// em `List.js`.
function DefaultPillSmall() {
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '11px',
                fontWeight: 700,
                lineHeight: 1,
                padding: '3px 9px',
                borderRadius: '20px',
                bgcolor: 'success.bg',
                color: 'success.main',
            }}
        >
            <Star size={10} strokeWidth={0} fill="currentColor" />
            Sim
        </Box>
    );
}

function PreviewRow({ label, value }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', fontSize: '12.5px', py: '6px', color: 'text.secondary' }}>
            <span>{label}</span>
            <Box component="b" sx={{ color: 'text.primary', textAlign: 'right' }}>{value}</Box>
        </Box>
    );
}

function StandaloneChecklistForm() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);
    const [searchParams, setSearchParams] = useSearchParams();
    const standaloneFieldSx = useStandaloneFieldSx();
    const isMobileShell = useIsMobileShell();
    useAppBarTitle(isMobileShell ? (isEdit ? 'Editar checklist' : 'Novo checklist') : '');

    const isSidebarMobile = useMediaQuery('(max-width:1023.95px)');
    const isSidebarExpanded = useRecoilValue(sidebarExpandedSelector);
    const actionFooterLeft = isSidebarMobile ? 0 : (isSidebarExpanded ? 264 : 76);

    const [loadingData, setLoadingData] = useState(true);
    const [saving, setSaving] = useState(false);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('success');

    const [name, setName] = useState('');
    const [nameError, setNameError] = useState('');
    const [description, setDescription] = useState('');
    const [isDefault, setIsDefault] = useState(false);
    const [originalIsDefault, setOriginalIsDefault] = useState(false);
    const [currentDefault, setCurrentDefault] = useState(null);

    // Grupos anexados ao template — [{ id, name, order }], mesmo shape do
    // payload `UpdateChecklist.groups`/`ChecklistGroup` de `fv-api`.
    const [groups, setGroups] = useState([]);
    // Catálogo de grupos reutilizáveis disponíveis (`GET /checklist/groups`).
    const [availableGroups, setAvailableGroups] = useState([]);
    // Contagem de itens por grupo (`{ [groupId]: count|null }`) — a API não
    // expõe `items_count` nem em `/checklist/groups` nem em `GET
    // /checklist/{id}` (`ChecklistGroup` só tem id/name/order), então a
    // contagem é obtida sob demanda via `GET /checklist/group/{id}`
    // (`GetGroupDetails`, já existente, usado por `GroupForm.js`) para cada
    // grupo do catálogo + cada grupo já anexado. Ver trade-off no relatório
    // da tarefa (N+1 requests, aceitável para catálogos pequenos; endpoint
    // dedicado com contagem agregada ficaria para uma iteração de backend
    // futura, não solicitada aqui).
    const [groupItemCounts, setGroupItemCounts] = useState({});
    const fetchedCountIdsRef = useRef(new Set());

    const [comboResetKey, setComboResetKey] = useState(0);
    const [draggingId, setDraggingId] = useState(null);
    const dragIdRef = useRef(null);

    const handleShowErrorSnackbar = (errorMessage) => {
        setSeverity('error');
        setMessage(parseApiErrorPayload(errorMessage));
        setOpenSnackbar(true);
    };

    const ensureItemCounts = async (ids) => {
        const toFetch = [...new Set(ids)].filter((gid) => gid && !fetchedCountIdsRef.current.has(gid));
        if (toFetch.length === 0) return;
        toFetch.forEach((gid) => fetchedCountIdsRef.current.add(gid));
        const entries = await Promise.all(toFetch.map(async (gid) => {
            try {
                const res = await httpClient.get(`/checklist/group/${gid}`);
                const items = Array.isArray(res.data?.group_inspection_items)
                    ? res.data.group_inspection_items.filter((it) => it.active !== false)
                    : [];
                return [gid, items.length];
            } catch (error) {
                console.error(`Erro ao obter contagem de itens do grupo ${gid}:`, error);
                return [gid, null];
            }
        }));
        setGroupItemCounts((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    };

    useEffect(() => {
        let active = true;
        const load = async () => {
            setLoadingData(true);
            try {
                const [checklistsRes, groupsRes] = await Promise.all([
                    httpClient.get('/checklist'),
                    httpClient.get('/checklist/groups'),
                ]);
                if (!active) return;

                const allChecklists = Array.isArray(checklistsRes.data) ? checklistsRes.data : [];
                setCurrentDefault(allChecklists.find((c) => c.is_default) || null);

                const catalog = Array.isArray(groupsRes.data) ? groupsRes.data : [];
                setAvailableGroups(catalog);

                let attachedGroups = [];
                if (id) {
                    const detailRes = await httpClient.get(`/checklist/${id}`);
                    if (!active) return;
                    const data = detailRes.data || {};
                    setName(data.name || '');
                    setDescription(data.description || '');
                    setIsDefault(Boolean(data.is_default));
                    setOriginalIsDefault(Boolean(data.is_default));
                    attachedGroups = Array.isArray(data.groups)
                        ? [...data.groups].sort((a, b) => (a.order || 0) - (b.order || 0))
                        : [];
                    setGroups(attachedGroups);
                }

                await ensureItemCounts([...catalog.map((g) => g.id), ...attachedGroups.map((g) => g.id)]);
            } catch (error) {
                console.error('Erro ao carregar dados do template de checklist:', error);
                handleShowErrorSnackbar(error.response?.data || error.message || 'Erro ao carregar dados do template de checklist');
            } finally {
                if (active) setLoadingData(false);
            }
        };
        load();
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Reanexa automaticamente um grupo recém-criado a partir do editor de
    // grupo (item 130, `?attachGroupId=` — ver `handleNewGroup` acima e
    // `StandaloneGroupForm#handleSave` em `GroupForm.js`) assim que o catálogo
    // (`GET /checklist/groups`) estiver carregado e já incluir o novo grupo.
    useEffect(() => {
        if (loadingData) return;
        const attachId = searchParams.get('attachGroupId');
        if (!attachId) return;
        setGroups((prev) => {
            if (prev.some((g) => g.id === attachId)) return prev;
            const found = availableGroups.find((g) => g.id === attachId);
            if (!found) return prev;
            return [...prev, { id: found.id, name: found.name, order: prev.length + 1 }];
        });
        ensureItemCounts([attachId]);
        const next = new URLSearchParams(searchParams);
        next.delete('attachGroupId');
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingData, availableGroups]);

    const itemsCountLabel = (groupId) => {
        const count = groupItemCounts[groupId];
        if (count === undefined) return 'Carregando itens…';
        if (count === null) return 'Itens indisponíveis';
        return `${count} ite${count === 1 ? 'm' : 'ns'} de inspeção`;
    };
    const itemsCountShort = (groupId) => {
        const count = groupItemCounts[groupId];
        if (count === null || count === undefined) return '—';
        return `${count} ite${count === 1 ? 'm' : 'ns'}`;
    };

    const totalItemsCount = groups.reduce((sum, g) => sum + (groupItemCounts[g.id] || 0), 0);

    const showDefaultSwapWarning = isDefault && !originalIsDefault && Boolean(currentDefault) && currentDefault.id !== id;

    const handleAddGroup = (group) => {
        if (!group || groups.some((g) => g.id === group.id)) return;
        setGroups((prev) => [...prev, { id: group.id, name: group.name, order: prev.length + 1 }]);
        ensureItemCounts([group.id]);
    };

    const handleRemoveGroup = (groupId) => {
        setGroups((prev) => prev.filter((g) => g.id !== groupId));
    };

    // Editor de grupo (item 130, `GroupForm.js`/`StandaloneGroupForm`) — navega
    // de verdade para o editor de estrutura do grupo (itens + respostas
    // OK/NOK/Manutenção), levando o id do template atual via `?tplId=` para
    // que o editor de grupo saiba para onde voltar ao salvar. Quando o
    // template ainda não foi salvo (`id` undefined), cai para o modo avulso
    // (`/checklist/group/add`/`/checklist/group/edt`) — o usuário reata o
    // grupo recém-criado via "Adicionar grupo existente" ao voltar.
    const handleNewGroup = () => {
        navigate(id ? `/checklist/${id}/group/new` : '/checklist/group/add');
    };
    const handleEditGroup = (groupIdToEdit) => {
        navigate(id ? `/checklist/${id}/group/edt?groupId=${groupIdToEdit}` : `/checklist/group/edt?groupId=${groupIdToEdit}`);
    };

    // Shell mobile (sem arrastar): reordena pela folha de ações da linha.
    const handleMoveGroup = (groupId, delta) => {
        setGroups((prev) => {
            const fromIdx = prev.findIndex((g) => g.id === groupId);
            const toIdx = fromIdx + delta;
            if (fromIdx === -1 || toIdx < 0 || toIdx >= prev.length) return prev;
            const next = [...prev];
            const [moved] = next.splice(fromIdx, 1);
            next.splice(toIdx, 0, moved);
            return next;
        });
    };

    // Drag-and-drop nativo HTML5 (sem biblioteca) — reordena `groups` ao
    // vivo enquanto arrasta, mesmo comportamento de
    // `prototipo-checklist-editor.html` (`onRowDragStart`/`onRowDragOver`/
    // `onRowDragEnd`). Padrão novo, documentado como extensão em
    // DESIGN-SYSTEM.md §5 ("Drag-and-drop de tabela reordenável").
    const handleRowDragStart = (e, groupId) => {
        dragIdRef.current = groupId;
        setDraggingId(groupId);
        e.dataTransfer.effectAllowed = 'move';
        try { e.dataTransfer.setData('text/plain', String(groupId)); } catch (err) { /* noop */ }
    };
    const handleRowDragOver = (e, overGroupId) => {
        e.preventDefault();
        const draggedId = dragIdRef.current;
        if (draggedId == null || draggedId === overGroupId) return;
        setGroups((prev) => {
            const fromIdx = prev.findIndex((g) => g.id === draggedId);
            const toIdx = prev.findIndex((g) => g.id === overGroupId);
            if (fromIdx === -1 || toIdx === -1) return prev;
            const next = [...prev];
            const [moved] = next.splice(fromIdx, 1);
            next.splice(toIdx, 0, moved);
            return next;
        });
    };
    const handleRowDragEnd = () => {
        dragIdRef.current = null;
        setDraggingId(null);
    };

    // Anel de progresso — 2 critérios: nome preenchido e ao menos 1 grupo
    // anexado (`prototipo-checklist-editor.html` `updateProgress()`).
    const RING_R = 23;
    const RING_CIRC = 2 * Math.PI * RING_R;
    const progressChecks = [Boolean(name.trim()), groups.length > 0];
    const progressPct = Math.round((progressChecks.filter(Boolean).length / progressChecks.length) * 100);
    const ringOffset = RING_CIRC - (RING_CIRC * progressPct / 100);

    const handleSave = async () => {
        const trimmedName = name.trim();
        if (!trimmedName) {
            setNameError('Informe o nome do template.');
            return;
        }
        setNameError('');
        setSaving(true);
        try {
            const trimmedGroups = groups.map((g, idx) => ({ id: g.id, name: g.name, order: idx + 1 }));
            const payload = {
                name: trimmedName,
                description: description.trim(),
                is_default: isDefault,
                groups: trimmedGroups,
            };
            let successMessage;
            if (isEdit) {
                // `PUT /checklist/{id}` (`UpdateChecklist`) já persiste o array
                // `groups` inteiro (delete+insert) — um único request basta.
                const response = await httpClient.put(`/checklist/${id}`, payload);
                if (!(response.status === 204 || response.status === 200)) {
                    handleShowErrorSnackbar(response.data || 'Erro ao atualizar template de checklist');
                    return;
                }
                successMessage = 'Template atualizado com sucesso!';
            } else {
                // `POST /checklist` (`CreateChecklist`) ignora silenciosamente
                // qualquer `groups` no payload — o input do handler
                // (`checklist.CreateChecklist`, `fv-api/checklist/checklist.go`)
                // só tem `name`/`description`/`enterprise_id`/`is_default`, sem
                // campo `groups` (diferente de `UpdateChecklist`, que tem). Como
                // este editor precisa permitir anexar grupos já na criação (item
                // 129), fazemos um segundo request (`PUT /checklist/{novoId}`,
                // que já suporta `groups`) logo em seguida quando há grupos
                // selecionados — sem exigir nenhuma mudança de contrato no
                // backend, só orquestrando dois endpoints já existentes.
                const response = await httpClient.post('/checklist', payload);
                if (!(response.status === 201 || response.status === 200)) {
                    handleShowErrorSnackbar(response.data || 'Erro ao criar template de checklist');
                    return;
                }
                const newId = response.data?.id;
                successMessage = 'Template cadastrado com sucesso!';
                if (newId && trimmedGroups.length > 0) {
                    try {
                        await httpClient.put(`/checklist/${newId}`, payload);
                    } catch (groupsError) {
                        console.error('Erro ao anexar grupos ao template recém-criado:', groupsError);
                        successMessage = 'Template cadastrado, mas houve falha ao anexar os grupos de inspeção — edite o template para tentar novamente.';
                        setSeverity('warning');
                        setMessage(successMessage);
                        setOpenSnackbar(true);
                        setTimeout(() => navigate('/vehicle_checklist'), 1600);
                        return;
                    }
                }
            }
            setSeverity('success');
            setMessage(successMessage);
            setOpenSnackbar(true);
            setTimeout(() => navigate('/vehicle_checklist'), 1200);
        } catch (error) {
            console.error('Erro ao salvar template de checklist:', error);
            handleShowErrorSnackbar(error.response?.data || error.message || 'Erro ao salvar template de checklist');
        } finally {
            setSaving(false);
        }
    };

    const cardSx = {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: `${theme.customRadius.card}px`,
        boxShadow: theme.customShadows.sm,
        backgroundColor: 'background.paper',
        p: '20px 22px',
        mb: '20px',
    };

    const availableOptionsToAdd = availableGroups.filter((g) => !groups.some((x) => x.id === g.id));

    const groupsTableSkeleton = (
        <Stack spacing={1.75}>
            <Skeleton variant="rounded" height={140} sx={{ borderRadius: '14px' }} />
            <Skeleton variant="rounded" height={260} sx={{ borderRadius: '14px' }} />
        </Stack>
    );

    if (isMobileShell) {
        return (
            <PageScaffold
                titlePageTarget={isEdit ? 'Editar' : 'Novo'}
                links={[{ title: 'Templates de Checklist', link: '/vehicle_checklist' }]}
                message={message}
                severity={severity}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
            >
                {loadingData ? (
                    <Stack spacing={1.75}>
                        <Skeleton variant="rounded" height={76} sx={{ borderRadius: '14px' }} />
                        <Skeleton variant="rounded" height={120} sx={{ borderRadius: '14px' }} />
                        <Skeleton variant="rounded" height={180} sx={{ borderRadius: '14px' }} />
                    </Stack>
                ) : (
                    <MobileChecklistForm
                        name={name}
                        onNameChange={(value) => { setName(value); if (nameError) setNameError(''); }}
                        nameError={nameError}
                        description={description}
                        onDescriptionChange={setDescription}
                        isDefault={isDefault}
                        onIsDefaultChange={setIsDefault}
                        swapWarning={showDefaultSwapWarning ? `Ao salvar, este template substituirá "${currentDefault?.name}" como padrão atual.` : ''}
                        groups={groups}
                        availableGroups={availableOptionsToAdd}
                        itemsLabel={itemsCountShort}
                        onAddGroup={handleAddGroup}
                        onNewGroup={handleNewGroup}
                        onEditGroup={handleEditGroup}
                        onRemoveGroup={handleRemoveGroup}
                        onMoveGroup={handleMoveGroup}
                        onCancel={() => navigate('/vehicle_checklist')}
                        onSave={handleSave}
                        saving={saving}
                    />
                )}
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            title={isEdit ? 'Editar Template de Checklist' : 'Novo Template de Checklist'}
            titlePageTarget={isEdit ? 'Editar' : 'Novo'}
            links={[{ title: 'Templates de Checklist', link: '/vehicle_checklist' }]}
            message={message}
            severity={severity}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
        >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* form-hero — voltar + título + anel de progresso. */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap', mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
                        <IconButton
                            onClick={() => navigate('/vehicle_checklist')}
                            title="Voltar para Templates de Checklist"
                            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', flexShrink: 0 }}
                        >
                            <ArrowLeft size={18} strokeWidth={2} />
                        </IconButton>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }} noWrap>
                                {isEdit ? `Editar template — ${name || ''}` : 'Novo template de checklist'}
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary' }}>
                                {isEdit
                                    ? `Atualize os dados de "${name}" e a ordem dos grupos de inspeção anexados.`
                                    : 'Defina o nome e a descrição do template, depois monte a ordem dos grupos de inspeção anexados.'}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
                        <Box sx={{ position: 'relative', width: 54, height: 54, flexShrink: 0 }}>
                            <svg viewBox="0 0 54 54" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
                                <circle cx="27" cy="27" r={RING_R} fill="none" strokeWidth="5" stroke={theme.palette.divider} />
                                <circle
                                    cx="27" cy="27" r={RING_R} fill="none" strokeWidth="5" strokeLinecap="round"
                                    stroke={theme.palette.primary.main}
                                    strokeDasharray={RING_CIRC}
                                    strokeDashoffset={ringOffset}
                                    style={{ transition: 'stroke-dashoffset .35s ease' }}
                                />
                            </svg>
                            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>
                                {progressPct}%
                            </Box>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Cadastro</Typography>
                            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>completo conforme você preenche</Typography>
                        </Box>
                    </Box>
                </Box>

                {loadingData ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px' }}>
                        {groupsTableSkeleton}
                        <Skeleton variant="rounded" height={340} sx={{ borderRadius: '18px' }} />
                    </Box>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px', alignItems: 'start', '& > *': { minWidth: 0 } }}>
                        <Box>
                            {/* CARD 1 — Dados do template. */}
                            <Box sx={cardSx}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography sx={{ fontSize: 15, fontWeight: 800, mb: '3px' }}>Dados do template</Typography>
                                    <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', maxWidth: 520, lineHeight: 1.5 }}>
                                        Nome e descrição identificam o template ao escolher um checklist numa ordem de serviço.
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <TextField
                                        autoFocus={!isEdit}
                                        fullWidth
                                        label={<>Nome do template <Box component="span" sx={{ color: 'error.main' }}>*</Box></>}
                                        value={name}
                                        onChange={(e) => { setName(e.target.value); if (nameError) setNameError(''); }}
                                        error={Boolean(nameError)}
                                        helperText={nameError}
                                        variant="outlined"
                                        inputProps={{ maxLength: 255 }}
                                        sx={standaloneFieldSx}
                                    />
                                </Box>

                                <Box sx={{ mb: 2.5 }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={3}
                                        label="Descrição"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        variant="outlined"
                                        inputProps={{ maxLength: 255 }}
                                        sx={standaloneFieldSx}
                                    />
                                </Box>

                                <Typography sx={{ fontSize: '10.5px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '10px' }}>
                                    Configuração
                                </Typography>
                                <Box sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px',
                                    p: '14px 16px', borderRadius: '14px', backgroundColor: 'background.subtle', border: '1px solid', borderColor: 'divider',
                                }}>
                                    <Box>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Definir como template padrão</Typography>
                                        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', lineHeight: 1.4, mt: '2px' }}>
                                            Usado automaticamente quando nenhum outro template é selecionado na ordem de serviço.
                                        </Typography>
                                    </Box>
                                    <Switch checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
                                </Box>
                                {showDefaultSwapWarning && (
                                    <Alert severity="info" sx={{ mt: 1.5 }}>
                                        Ao salvar, este template substituirá &quot;{currentDefault?.name}&quot; como padrão atual.
                                    </Alert>
                                )}
                            </Box>

                            {/* CARD 2 — Grupos de inspeção do template (drag-and-drop). */}
                            <Box sx={cardSx}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography sx={{ fontSize: 15, fontWeight: 800, mb: '3px' }}>Grupos de inspeção do template</Typography>
                                    <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', maxWidth: 560, lineHeight: 1.5 }}>
                                        Arraste as linhas pela alça <Box component="b" sx={{ color: 'text.primary' }}>⠿</Box> para reordenar — esta é a ordem em que os grupos aparecem ao preencher o checklist.
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap', mb: 2 }}>
                                    <Box sx={{ flex: '1 1 260px', maxWidth: 420 }}>
                                        <SelectAutocomplete
                                            key={comboResetKey}
                                            options={availableOptionsToAdd}
                                            value={''}
                                            onChange={(groupId) => {
                                                const group = availableGroups.find((g) => g.id === groupId);
                                                if (group) {
                                                    handleAddGroup(group);
                                                    setComboResetKey((k) => k + 1);
                                                }
                                            }}
                                            label="Adicionar grupo existente"
                                            variant="outlined"
                                            size="medium"
                                            getOptionLabel={(option) => option?.name || ''}
                                            getOptionValue={(option) => option?.id || ''}
                                            noOptionsText={
                                                availableGroups.length > 0 && availableOptionsToAdd.length === 0
                                                    ? 'Todos os grupos do catálogo já foram anexados.'
                                                    : 'Nenhum grupo encontrado.'
                                            }
                                            disabled={availableGroups.length === 0}
                                            renderOption={(props, option) => (
                                                <li {...props} key={option.id}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', py: '2px' }}>
                                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>{option.name}</Typography>
                                                        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{itemsCountLabel(option.id)}</Typography>
                                                    </Box>
                                                </li>
                                            )}
                                            sx={standaloneFieldSx}
                                        />
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<Plus size={14} strokeWidth={2} />}
                                        onClick={handleNewGroup}
                                    >
                                        Novo grupo de inspeção
                                    </Button>
                                </Box>

                                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px', overflow: 'hidden' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ width: 40, bgcolor: 'background.subtle' }} />
                                                <TableCell sx={{ width: 44, bgcolor: 'background.subtle', fontSize: '10.5px', fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.05em' }}>#</TableCell>
                                                <TableCell sx={{ bgcolor: 'background.subtle', fontSize: '10.5px', fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.05em' }}>Grupo de inspeção</TableCell>
                                                <TableCell align="right" sx={{ bgcolor: 'background.subtle', fontSize: '10.5px', fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.05em' }}>Ações</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {groups.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={4} sx={{ border: 'none' }}>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: '36px 20px', color: 'text.disabled', textAlign: 'center', gap: '10px' }}>
                                                            <Box sx={{ width: 56, height: 56, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <Info size={22} strokeWidth={1.6} />
                                                            </Box>
                                                            <Typography sx={{ color: 'text.primary', fontSize: '13.5px', fontWeight: 700 }}>Nenhum grupo de inspeção anexado</Typography>
                                                            <Typography sx={{ fontSize: '12.5px', maxWidth: 340 }}>
                                                                Use o campo "Adicionar grupo existente" acima para anexar um grupo já cadastrado ao template.
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ) : groups.map((g, idx) => (
                                                <TableRow
                                                    key={g.id}
                                                    draggable
                                                    onDragStart={(e) => handleRowDragStart(e, g.id)}
                                                    onDragOver={(e) => handleRowDragOver(e, g.id)}
                                                    onDragEnd={handleRowDragEnd}
                                                    sx={{
                                                        opacity: draggingId === g.id ? 0.35 : 1,
                                                        cursor: 'grab',
                                                        '&:last-child td': { borderBottom: 'none' },
                                                    }}
                                                >
                                                    <TableCell sx={{ color: 'text.disabled', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Arraste para reordenar">
                                                        <GripVertical size={16} />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{
                                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '7px',
                                                            backgroundColor: 'background.subtle', border: '1px solid', borderColor: 'divider', fontSize: 11, fontWeight: 800, color: 'text.secondary',
                                                        }}>
                                                            {idx + 1}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>{g.name}</Typography>
                                                        <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: '1px' }}>{itemsCountLabel(g.id)}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                            <IconButton
                                                                size="small"
                                                                title="Editar grupo (itens e respostas padrão)"
                                                                onClick={() => handleEditGroup(g.id)}
                                                                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', color: 'text.disabled', '&:hover': { color: 'primary.main', borderColor: 'primary.main' } }}
                                                            >
                                                                <Pencil size={14} strokeWidth={2} />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                title="Remover do template"
                                                                onClick={() => handleRemoveGroup(g.id)}
                                                                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', color: 'text.disabled', '&:hover': { color: 'error.main', borderColor: 'error.main', backgroundColor: 'error.bg' } }}
                                                            >
                                                                <Trash2 size={14} strokeWidth={2} />
                                                            </IconButton>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* groups-note — fronteira de escopo com o editor de grupo (item 124). */}
                                <Box sx={{
                                    display: 'flex', alignItems: 'flex-start', gap: '10px', mt: 2, p: '12px 14px',
                                    borderRadius: '12px', backgroundColor: 'background.subtle', border: '1px solid', borderColor: 'divider',
                                }}>
                                    <Info size={16} strokeWidth={2} color={theme.palette.text.disabled} style={{ marginTop: 1, flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.5 }}>
                                        <Box component="span" sx={{ display: 'block', fontWeight: 700, color: 'text.primary', fontSize: '12.5px', mb: '2px' }}>
                                            Escopo deste editor
                                        </Box>
                                        Aqui você escolhe quais grupos fazem parte do template e em que ordem. A criação/edição dos itens dentro de cada grupo (e suas opções de resposta OK/NOK/Manutenção) é feita numa tela separada — use "Editar grupo"/"Novo grupo de inspeção" acima, ou acesse{' '}
                                        <Box
                                            component="span"
                                            role="link"
                                            tabIndex={0}
                                            onClick={() => navigate('/checklist/groups')}
                                            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/checklist/groups'); }}
                                            sx={{ color: 'primary.main', fontWeight: 700, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                        >
                                            Grupos de Inspeção
                                        </Box>{' '}
                                        para gerenciar o catálogo reutilizável completo (criar, duplicar, excluir).
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* PREVIEW STICKY — pré-visualização estrutural do checklist. */}
                        <Box sx={{ position: { md: 'sticky' }, top: { md: 20 } }}>
                            <Typography sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.disabled', mb: 1.25 }}>
                                Pré-visualização ao vivo
                            </Typography>
                            <Box sx={{
                                border: '1px solid', borderColor: 'divider', borderRadius: `${theme.customRadius.hero - 2}px`,
                                backgroundColor: 'background.paper', boxShadow: theme.customShadows.md, overflow: 'hidden', mb: 1.75,
                            }}>
                                <Box sx={{ p: '16px 18px' }}>
                                    <PreviewRow label="Grupos anexados" value={groups.length} />
                                    <PreviewRow label="Itens de inspeção (total)" value={totalItemsCount} />
                                    <PreviewRow label="Template padrão" value={isDefault ? <DefaultPillSmall /> : 'Não'} />
                                </Box>
                                <Box sx={{ height: '1px', bgcolor: 'divider', mx: '18px' }} />
                                <Box sx={{ p: '10px 14px 16px' }}>
                                    {groups.length === 0 ? (
                                        <Typography sx={{ fontSize: 11, color: 'text.disabled', p: '14px 4px' }}>
                                            Nenhum grupo anexado ainda — a pré-visualização aparecerá aqui conforme você adicionar grupos ao lado.
                                        </Typography>
                                    ) : groups.map((g) => (
                                        <Box key={g.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px', mb: '10px', overflow: 'hidden', '&:last-child': { mb: 0 } }}>
                                            <Box sx={{
                                                p: '10px 13px', background: theme.palette.gradient.brand, color: '#fff', fontSize: 12, fontWeight: 800,
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
                                            }}>
                                                <span>{g.name}</span>
                                                <Box component="span" sx={{ fontSize: '10.5px', fontWeight: 700, bgcolor: 'rgba(255,255,255,.2)', px: '8px', py: '3px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
                                                    {itemsCountShort(g.id)}
                                                </Box>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                            <Typography sx={{ fontSize: 11, color: 'text.disabled', lineHeight: 1.5 }}>
                                É assim que os grupos aparecerão, em ordem, ao preencher este checklist numa ordem de serviço (mesmo componente da aba Checklist da Operação da OS). Os itens de cada grupo não são exibidos aqui — são definidos no editor de grupo, tela separada deste editor de template.
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Espaçador para compensar o rodapé fixo abaixo. */}
                <Box sx={{ height: 'calc(84px + env(safe-area-inset-bottom, 0px))' }} aria-hidden="true" />

                <Box sx={{
                    position: 'fixed', left: actionFooterLeft, right: 0, bottom: 0, zIndex: theme.zIndex.appBar,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap',
                    backgroundColor: 'background.paper', borderTop: '1px solid', borderColor: 'divider',
                    boxShadow: '0 -6px 20px rgba(15,23,42,.06)', px: 3, py: 1.75, transition: 'left .2s ease',
                    pl: 'calc(24px + env(safe-area-inset-left, 0px))', pr: 'calc(24px + env(safe-area-inset-right, 0px))', pb: 'calc(14px + env(safe-area-inset-bottom, 0px))',
                }}>
                    <Typography sx={{ fontSize: '12.5px', color: 'text.disabled' }}>Campos com * são obrigatórios</Typography>
                    <Stack direction="row" spacing={1.5}>
                        <Button variant="outlined" onClick={() => navigate('/vehicle_checklist')} disabled={saving}>
                            Cancelar
                        </Button>
                        <Button variant="contained" onClick={handleSave} disabled={saving || loadingData}>
                            {saving ? 'Salvando...' : (isEdit ? 'Salvar alterações' : 'Salvar template')}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </PageScaffold>
    );
}

export default function ChecklistForm(props) {
    if (props.embedded) {
        return <LegacyEmbeddedChecklistForm {...props} />;
    }
    return <StandaloneChecklistForm />;
}
